import type { PostHogErrorBoundaryFallbackProps } from "@posthog/react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { NAMESPACES } from "@/i18n/constants";

interface ErrorPageRecoveryProps extends PostHogErrorBoundaryFallbackProps {}

/**
 * Root error-boundary fallback. Native layout + CossUI Button, styled with Tailwind.
 * Kept free of App providers so it still renders when the tree fails.
 */
function ErrorPageRecovery(_props: ErrorPageRecoveryProps) {
    const { t } = useTranslation(NAMESPACES.PAGES.ERROR_PAGE_RECOVERY);

    const handleRefresh = () => {
        // Hard navigation to home — full reload and leave the failing route.
        window.location.replace("/");
    };

    return (
        <HelmetProvider>
            <Helmet>
                <title>{t("pageTitle", "Something went wrong")}</title>
                <meta
                    name={"description"}
                    content={t("metaDescription", "Something went wrong while displaying this page.")}
                />
            </Helmet>

            <main
                className={
                    "flex min-h-dvh w-full flex-col items-center justify-center bg-background p-8 text-center max-sm:p-4"
                }
            >
                <section
                    className={
                        "flex w-full max-w-3xl flex-col items-center gap-4 rounded-lg bg-card px-8 py-12 text-card-foreground shadow-md max-sm:px-4 max-sm:py-6"
                    }
                    role={"alert"}
                >
                    <h1 className={"mb-1 text-4xl font-bold text-foreground max-sm:font-medium"}>{t("error-code")}</h1>

                    <img
                        src={"/assets/images/404-illustration.webp"}
                        alt={t("errorIllustration", "Error illustration")}
                        className={"my-4 h-auto max-w-xs object-contain max-sm:my-2"}
                    />

                    <h2 className={"text-center text-2xl font-medium text-foreground max-sm:mt-4"}>
                        {t("error-message-header")}
                    </h2>

                    <p className={"max-w-xl text-center text-base text-muted-foreground max-sm:mb-6"}>
                        {t("error-message-description")}
                    </p>

                    <div className={"flex gap-4 max-sm:w-full max-sm:flex-col"}>
                        <Button
                            type={"button"}
                            variant={"outline"}
                            className={"min-w-40 max-sm:w-full"}
                            onClick={handleRefresh}
                        >
                            {t("back-to-home")}
                        </Button>
                    </div>
                </section>
            </main>
        </HelmetProvider>
    );
}

export default ErrorPageRecovery;
